<template>
  <PageView
    :application="application"
    class="with-submenu"
  >
    <SubMenu
      :aria-label="$t('menu.aria-sub-menu')"
      :paths="subMenuPaths"
      :root="application.localName"
      role="navigation"
    />
    <TitleAndDescription
      :application="application"
      :local-title="
        type[type.length - 2] === 'dataTypes'
          ? $t('titles.datatypes-table', {refName: application.localRefName})
          : $t('titles.references-table', {refName: application.localRefName})
      "
      :local-description="application.localRefDescription"
      :local-name="application.localRefName"
    />
    <div class="contentView">
      <div
        id="tagsCollapseReal"
        class="column tagsCollapseReal"
      >
        <TagsCollapse
          v-if="hasTags"
          :tags="tags"
          @change:tag="changeTagSelected($event)"
          @change:all-tags="changeAllValueTags($event)"
        />
      </div>
      <div v-if="state !== 'NO-RIGHTS'">
        <!--
          UI cohérente du bloc filtre : la card "Filter" reste rendue dès
          que totalRows > 0 ( header / collapse toujours visibles ) . Le
          contenu interne ( filtres réels ou placeholder "no-criteria" )
          est géré par FiltersDataCollapse lui-même via un délai de
          1500ms post-mount ( cf NO_CRITERIA_DELAY_MS ) qui couvre le
          fetch /filters typique sans flash trompeur , ni saut entre 2
          layouts ( ex spinner externe puis card distincte - cf
          screenshots user 46/47 ) .
        -->
        <FiltersDataCollapse
          v-if="totalRows > 0"
          :loading="filterBlockLoading"
          :data-refreshing="filterBlockRefreshing"
          :data-resolved-once="dataResolvedOnce"
          :refresh-label-key="refreshLabelKey"
          :filter-list="filterLists"
          :application="application"
          :application-name="applicationName"
          :columns-to-be-shown="dataColumnsForFilters"
          :data-id="dataId"
          :is-data="isSup500"
          :list-reference-value-for-all-references="listReferenceValueForAllReferences"
          :is-ref-link-to="referenceTypeForReferencingColumns"
          :result-count="rows.length"
          :has-searched="hasSearched"
          @show-search="searchData($event)"
          @download-search="recalculate($event, true)"
          @clear-search="resetFilters($event)"
          @remove-tag="removeTag($event)"
        />
        <!--
          Bandeau "limite à 500 lignes" : affiché tant que l'utilisateur
          ne l'a pas fermé. La fermeture est mémorisée dans sessionStorage
          ( clé OA_DISMISS_INFO_500 ) pour qu'il ne réapparaisse plus
          dans la même session de navigation , quel que soit le datatype
          consulté ; au prochain login ( ou rechargement à froid ) il
          revient. Le bandeau garde son `closable` natif Buefy : on
          intercepte simplement l'évènement @close pour persister.
        -->
        <b-notification
          v-if="isSup500 && !info500Dismissed && !isLoading"
          type="is-warning is-light"
          size="is-small"
          has-icon
          aria-close-label="Close notification"
          class="border-notif-warning-light top-info-light"
          @close="dismissInfo500"
        >
          {{ $t("dataTable.info-extrait") }}
        </b-notification>
        <!--
          Spinner principal du chargement des données : message i18n
          explicite à côté du spinner pour signaler clairement à
          l'utilisateur ce qu'il attend ( la sortie d'oa-live , i.e.
          la lecture des 500 lignes échantillonnées ).
        -->
        <div
          v-if="isLoading"
          class="data-loading"
        >
          <LoadingAnimate :size="'is-large'" />
          <p class="data-loading-message">
            {{ $t("dataTable.loading-data") }}
          </p>
        </div>
        <!-- #476 - Message quand un filtre ne retourne aucun resultat.
             hasSearched distingue "pas encore charge" de "filtre applique avec 0 resultats". -->
        <b-notification
          v-if="!isLoading && hasSearched && rows.length === 0"
          type="is-info is-light"
          size="is-small"
          icon="info-circle"
          has-icon
          :closable="false"
          class="border-notif-info-light top-info-light"
        >
          {{ $t("dataTable.no-result") || "Aucun résultat pour ces critères de filtrage." }}
        </b-notification>
        <div v-else-if="data && dataColumnsToBeShown && !isLoading && totalRows > 0">
          <!-- TODO : retrait de l'option de l'horzontalisation du tableau -->
          <!-- <b-field v-if="canHorizontalize && !isSup500">
            <b-radio-button
              v-model="horizontalDisplay"
              :native-value="false"
              type="is-dark"
              :aria-label="$t('dataTypesManagement.verticalized')"
              @change="changeDisplay(horizontalDisplay)"
            >
              <b-icon icon="arrows-alt-v" />
              <span>{{ $t("dataTypesManagement.verticalized") }}</span>
            </b-radio-button>
            <b-radio-button
              v-model="horizontalDisplay"
              :native-value="true"
              type="is-dark"
              :aria-label="$t('dataTypesManagement.horizontalized')"
              @change="changeDisplay(horizontalDisplay)"
            >
              <b-icon icon="arrows-alt-h" />
              <span>{{ $t("dataTypesManagement.horizontalized") }}</span>
            </b-radio-button>
          </b-field> -->
          <div class="table-header-row">
            <p
              v-if="isSup500"
              class="columns subtitle titleSubmission"
              :aria-label="$t('dataTable.show_500')"
            >
              {{ $t('dataTable.show_500') }}
            </p>
            <!--
              Bloc droit du header de tableau , 2 contrôles côte-à-côte :
              le switch Tier 3 ( "Tout déplier" ) toujours visible , et
              le sélecteur "Lignes par page" rendu uniquement si la
              pagination est utile ( rows >= 20 ). Séparateur visuel
              entre les deux quand les deux sont rendus.
            -->
            <div class="table-header-controls">
              <!--
                Tier 3 : switch global "Tout déplier / Tout replier".
                Toujours rendu , peu importe le nombre de lignes : un
                tableau de 3 lignes contenant des GeoJSON longs a aussi
                besoin du toggle. Son état ON/OFF reflète isAllExpanded
                ( = toutes les cellules truncatable du tableau sont
                dépliées ) ; cliquer le bascule via toggleAll qui
                décide bulk-add ou bulk-remove selon l'état courant.
              -->
              <!--
                Switch global : libellé visible STATIQUE ( nom de la
                fonctionnalité = "Tout déplier" ) et état ON/OFF
                géré par le switch lui-même. Tooltip ( title ) et
                aria-label DYNAMIQUES , orientés action , décrivent
                ce qui va se passer si on clique. Cela évite la
                confusion " est-ce l'état courant ou l'action ? "
                signalée par l'utilisateur ( image #364 ).
              -->
              <b-switch
                :model-value="isAllExpanded()"
                size="is-small"
                type="is-primary"
                class="expand-all-switch"
                :title="isAllExpanded() ? $t('dataTable.collapse-all') : $t('dataTable.expand-all')"
                :aria-label="isAllExpanded() ? $t('dataTable.collapse-all') : $t('dataTable.expand-all')"
                @update:model-value="toggleAll()"
              >
                <span class="columns subtitle titleSubmission rows-per-page-label">
                  {{ $t('dataTable.expand-all') }}
                </span>
              </b-switch>
              <!--
                Sélecteur du nombre de lignes par page. Masqué dès qu'il
                ne sert à rien : sous le seuil de la plus petite option
                ( 20 ) , aucune des valeurs disponibles n'a d'effet
                visible , autant ne pas encombrer l'UI.
              -->
              <template v-if="rows.length >= 20">
                <span
                  class="table-header-divider"
                  aria-hidden="true"
                />
                <label
                  for="rows-per-page-select"
                  class="columns subtitle titleSubmission rows-per-page-label"
                >
                  {{ $t('dataTable.rows-per-page') }}
                </label>
                <!--
                  L'option "ALL" ( = 500 ) a été retirée : <b-table> Buefy
                  ne virtualise pas , chaque cellule rend un composant Vue
                  lourd ; à 500 lignes × ~12 colonnes on instancie ~6000
                  composants , ce qui ralentit fortement le navigateur.
                  Cap à 100. Liaison contrôlée ( :model-value +
                  @update:model-value ) plutôt que v-model pour que
                  onPerPageChange puisse lever le spinner avant la mise
                  à jour effective.
                -->
                <b-select
                  id="rows-per-page-select"
                  :model-value="perPage"
                  size="is-small"
                  :aria-label="$t('dataTable.rows-per-page')"
                  @update:model-value="onPerPageChange"
                >
                  <option :value="20">
                    20
                  </option>
                  <option :value="50">
                    50
                  </option>
                  <option :value="100">
                    100
                  </option>
                </b-select>
              </template>
            </div>
          </div>
          <!--
            Wrapper en position relative pour héberger l'overlay du spinner
            ( pagination ) au-dessus du <b-table>. Le tableau reste rendu
            derrière pour que son DOM soit déjà patché quand le spinner
            disparait ( voir onPerPageChange dans useDataTable.ts ).
          -->
          <div class="table-area">
            <LoadingAnimate
              v-if="paginationLoading"
              :size="'is-large'"
              class="pagination-overlay"
            />
            <b-table
              id="filtreTable"
              class="tableData"
              :data="rows"
              :height="tableHeight"
              is-focusable
              is-hoverable
              :sticky-header="isSticky"
              striped
              :paginated="totalRows > perPage"
              :per-page="perPage"
              :current-page="currentPage"
              :total="totalRows"
              :aria-current-label="$t('menu.aria-curent-page')"
              :aria-label="$t('menu.aria-pagination')"
              :aria-next-label="$t('menu.aria-next-page')"
              :aria-previous-label="$t('menu.aria-previous-page')"
              :default-sort="defaultSort"
              @sort="onSort"
            >
              <!-- #475 - sortable : Buefy n'affiche pas les fleches de tri quand le slot #header
                 est override. On les ajoute manuellement via b-icon dans chaque branche du slot. -->
              <b-table-column
                v-for="(column, index) in dataColumnsToBeShown"
                :key="column.id + '_' + index"
                :field="'values.' + column.id"
                :label="column.getHeader(application, dataId)"
                sortable
                sort-icon="arrow-up"
                sort-icon-size="is-small"
                :aria-label="column.id + '_' + index"
              >
                <template #header="{ column: bColumn }">
                  <!--
                  Wrapper flex pour pousser le bouton Tier 4 ( expand
                  colonne ) à l'extrême droite du header. Le contenu
                  existant ( label + caret de tri , éventuellement les
                  composants pattern/qualifier ) reste dans .header-main ;
                  .col-expand-toggle est en margin-left: auto pour aller
                  à droite , indépendamment de la branche v-if/v-else
                  rendue à l'intérieur.
                -->
                  <div class="header-flex">
                    <div class="header-main">
                      <div
                        v-if="column.tags"
                        class="column"
                      >
                        <TagsInfos
                          :info-parent="column"
                          :tags-column="Object.keys(tags).length !== 0 ? tags : {}"
                        />
                        <DatasPatternLink
                          v-if="'PatternComponent' === column.type"
                          :application="application"
                          :column-id="column.id"
                          :column-title="column.id"
                          :data-id="dataId"
                          :info="column.type === 'PatternComponent'"
                          :is-sticky="isSticky"
                          :info-values="
                            column.getColumnQualifiersMap(
                              application,
                              horizontalDisplay ? dataId : column.componentKey,
                              rows[0],
                              horizontalDisplay
                            )
                          "
                          :loaded-references-by-key="{}"
                          :pattern-checker-date-ref="patternCheckerDateRef"
                          :value="
                            column.getColumnQualifiersMap(
                              application,
                              horizontalDisplay ? dataId : column.componentKey,
                              rows[0],
                              horizontalDisplay
                            )
                          "
                          :with-qualifier="horizontalDisplay"
                          @change-sticky="changeValueSticky($event)"
                        />
                        <div
                          v-else-if="'patternQualifierComponent' === column.type"
                          :aria-label="column.getHeader(application, dataId)"
                        >
                          {{ column.getHeader(application, dataId) }}
                        </div>
                        <div
                          v-else
                          :aria-label="column.getHeader(application, dataId)"
                        >
                          {{ column.getHeader(application, dataId) }}
                        </div>
                      </div>
                      <div
                        v-else
                        class="column"
                        :aria-label="column.getHeader(application, dataId)"
                      >
                        {{ column.getHeader(application, dataId) }}
                        <DatasDynamicLink
                          v-if="'PatternComponent' === column.type"
                          :application="application"
                          :column-id="column.id"
                          :info="column.type === 'PatternComponent'"
                          :info-values="column.getColumnQualifiersMap(application,null, null, horizontalDisplay)"
                          :loaded-references-by-key="{}"
                          :reference-type="column.referenceType"
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      class="col-expand-toggle"
                      :title="isColumnAllExpanded(column, rows) ? $t('dataTable.toggle-column-collapse') : $t('dataTable.toggle-column-expand')"
                      :aria-label="isColumnAllExpanded(column, rows) ? $t('dataTable.toggle-column-collapse') : $t('dataTable.toggle-column-expand')"
                      :aria-pressed="isColumnAllExpanded(column, rows)"
                      @click.stop="toggleColumn(column, rows)"
                    >
                      <b-icon
                        :icon="isColumnAllExpanded(column, rows) ? 'compress-alt' : 'expand-alt'"
                        size="is-small"
                      />
                    </button>
                    <!--
                      Indicateur de tri double-triangle , placé en
                      dernière position du header ( après le bouton
                      plier/déplier ) - extrême droite . Centré
                      verticalement via {@code align-items: center} de
                      {@code .header-flex} .
                    -->
                    <SortDoubleTriangle
                      class="sort-tri-anchor"
                      :direction="sortDirectionFor('values.' + column.id)"
                    />
                  </div>
                </template>
                <template #default="props">
                  <!--
                  Tier 1 du toggle d'expand cellule : on tronque la valeur
                  brute à 120 caractères ( + "..." ) pour éviter les
                  cellules de 30 lignes ( cas GeoJSON , descriptions
                  longues ). Le bouton expand ne s'affiche QUE si la
                  valeur dépasse réellement le seuil
                  ( isCellTruncatable ). Cliqué , il déplie cette
                  cellule seule ; il est aussi forcé déplié si Tier 3
                  ( switch global ) ou Tier 4 ( colonne ) le sont.
                  Le seuil 120 ne s'applique qu'au cas DatasLink ( la
                  vraie source des longs textes ) ; les autres branches
                  rendent du contenu structuré ( chips , patterns ) qui
                  reste tel quel.
                -->
                  <!--
                  Composant <TruncatableCell> autonome ( cf.
                  components/common/TruncatableCell.vue ) : encapsule
                  toute la logique de troncature visuelle , l'icône ▾ ,
                  le wrap multi-ligne et les overrides Bulma. Stateless ,
                  l'état d'expand vient du composable useCellExpand
                  ( v-model:expanded ). Le slot scope expose
                  displayedText pour que les composants enfants
                  ( DatasLink etc. ) reçoivent la chaine déjà tronquée
                  / complète selon l'état.
                -->
                  <TruncatableCell
                    :text="cellRawText(props.row, column)"
                    :expanded="isCellExpanded(props.row, column)"
                    @update:expanded="toggleCell(props.row, column)"
                  >
                    <template #default="{ text }">
                      <DatasDynamicLink
                        v-if="
                          column.type === 'DynamicComponent' && props.row.values[column.id] !== undefined
                        "
                        :application="application"
                        :column-id="column.id"
                        :info="column.type === 'DynamicComponent'"
                        :info-values="props.row.values[column.id]"
                        :loaded-references-by-key="{}"
                        :reference-type="column.referenceType"
                      />
                      <DatasManyLink
                        v-else-if="column.multiplicity === MANY"
                        :application="application"
                        :column="column"
                        :column-id="column.id"
                        :component="props.row"
                        :displays-for-row="filterLists"
                        :info-values="props.row.values[column.id]"
                        :loaded-references-by-key="{}"
                        :multiplicity="column.multiplicity === MANY"
                        :reference-type="addRefLinkedTo(column.id, column.refLinkedTo) || ''"
                      />
                      <DatasLink
                        v-else-if="column.id !== '#' && column.getColumnValue(props.row) !== undefined"
                        :application="application"
                        :column="column"
                        :column-id="getRefColumnId(props.row, column)"
                        :column-title="getColumnNameView(column.id, application, dataId, column)"
                        :component="props.row"
                        :display-value="text"
                        :loaded-references-by-key="{}"
                        :pattern-checker-date="column.datePattern || patternCheckerDateRef(application, column.title, dataId)"
                        :reference-type="(column.refLinkedTo as string) || ''"
                        :value="'' + column.getColumnValue(props.row)"
                        :row="props.row"
                      />
                      <div
                        v-else-if="column.getColumnValue(props.row) !== undefined"
                        class="columns"
                      >
                        <!-- TODO - rebrancher la suppression de ligne -->
                        <a
                          @click="askDeletionConfirmation(rows[tableValues.indexOf(props.row)]?.naturalKey)"
                        >
                          <b-icon
                            class="clickable"
                            icon="times-circle"
                            size="is-small"
                            type="is-danger"
                          />
                        </a>
                        <b-collapse
                          :model-value="false"
                          class="column"
                          tabindex="0"
                          :aria-label="rows[tableValues.indexOf(props.row)]?.naturalKey"
                        >
                          <template #trigger>
                            <b-button
                              :label="'' + (tableValues.indexOf(props.row) + 1 + params.offset)"
                              aria-controls="contentIdForA11y1"
                              type="is-small"
                            />
                          </template>
                          {{ rows[tableValues.indexOf(props.row)]?.naturalKey }}
                        </b-collapse>
                      </div>
                    </template>
                  </TruncatableCell>
                </template>
              </b-table-column>
            </b-table>
          </div>
        </div>
        <!--
          Banner "About N records available" : affiche UNIQUEMENT a la
          1ere entree dans le datatype / referentiel ( spec image #98 ) .
          Le flag `hasUserInteracted` bascule a true des le 1er clic
          Consulter ou Reinitialiser via les wrappers searchData /
          resetFilters dans useDataTable . Apres , ce bloc est masque
          definitivement pendant cette session : la valeur totalRows
          n'est plus l'info pertinente , c'est le contenu du tableau ou
          le message no-result qui prennent le relais . Reste dans la
          chaine v-if/v-else-if existante pour conserver la priorite
          tableau > no-result > banner ( fallback ) . Lorsque l'init
          fait apparaitre un tableau , le v-else-if precedent matche
          en premier et ce banner reste masque - comportement coherent
          avec le bandeau "info-extrait" qui est lui aussi un complement
          d'info initial sans valeur ajoutee apres interaction .
        -->
        <div v-else-if="!hasUserInteracted">
          <b-message
            :closable="false"
            has-icon
            size="is-medium"
            type="is-primary"
            :aria-label="$t('applications.register-rows', totalRows)"
          >
            {{ $t("applications.register-rows", totalRows) }}
          </b-message>
        </div>
      </div>

      <div v-else-if="state === 'NO-RIGHTS'">
        <b-message
          :closable="false"
          has-icon
          size="is-medium"
          type="is-danger"
          :aria-label="$t('applications.no-right-for-application')"
        >
          {{ $t("applications.no-right-for-application") }}
        </b-message>
        <b-button
          class="mt-3"
          type="is-primary"
          :aria-label="$t('dataTypeAuthorizations.request')"
          @click="router().push('../authorizationsRequest/new')"
        >
          {{ $t("dataTypeAuthorizations.request") }}
        </b-button>
      </div>
      <div v-else>
        <b-message
          :closable="false"
          has-icon
          size="is-medium"
          type="is-primary"
          :aria-label="$t('applications.register-rows', totalRows)"
        >
          {{ $t("applications.register-rows", totalRows) }}
        </b-message>
      </div>
    </div>
  </PageView>
</template>

<script lang="ts">
// ─────────────────────────────────────────────────────────────────────────────
// DataTableView.vue — vue allégée
// Toute la logique métier est dans @/composable/data/useDataTable.ts
// ─────────────────────────────────────────────────────────────────────────────
import SubMenu from "@/components/common/SubMenu.vue";
import DatasLink from "@/components/datas/DatasLink.vue";
import DatasManyLink from "@/components/datas/DatasManyLink.vue";
import DatasDynamicLink from "@/components/datas/DatasDynamicLink.vue";
import TagsCollapse from "@/components/common/TagsCollapse.vue";
import LoadingAnimate from "@/components/common/LoadingAnimate.vue";
import PageView from "../common/PageView.vue";
import FiltersDataCollapse from "@/components/common/provider/FiltersDataCollapse.vue";
import TagsInfos from "@/components/common/TagsInfos.vue";
import DatasPatternLink from "@/components/datas/DatasPatternLink.vue";
import TitleAndDescription from "@/components/common/TitleAndDescription.vue";
import TruncatableCell from "@/components/common/TruncatableCell.vue";
import SortDoubleTriangle from "@/components/common/SortDoubleTriangle.vue";
import router from "@/router";
import "@/style/views/data/DataTableView.scss";
import { BButton, BCollapse, BField, BIcon, BMessage, BNotification, BRadioButton, BSelect, BTable, BTableColumn } from "buefy";
import { useDataTable } from "@/composable/data/useDataTable";
import { useCellExpand } from "@/composable/data/useCellExpand";
import type { DataRowResult } from "@/types/generated/UsedGeneratedTypes";
import type { Component } from "@/types/front/LocalComponent";

export default {
  name: "DataTableView",
  components: {
    BMessage,
    BButton,
    BCollapse,
    BTableColumn,
    BNotification,
    BSelect,
    BTable,
    BIcon,
    BField,
    BRadioButton,
    TitleAndDescription,
    DatasPatternLink,
    TagsInfos,
    FiltersDataCollapse,
    LoadingAnimate,
    PageView,
    SubMenu,
    TagsCollapse,
    TruncatableCell,
    SortDoubleTriangle,
    DatasLink,
    DatasManyLink,
    DatasDynamicLink,
  },
  props: {
    applicationName: {
      type: String,
      required: true,
    },
    dataId: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const dt = useDataTable(props);
    // ── Cell expand ( 3 niveaux ) ────────────────────────────────────
    // La logique d'expand des cellules ( valeur > 120 chars tronquée
    // + ▾ pour déplier , ▾ par colonne dans le header , switch global
    // dans la barre ) est entièrement encapsulée dans le composable
    // useCellExpand , typé , testé , et indépendant de la forme des
    // données. On lui fournit les getters de clé et le getter de
    // texte affiché ( getDisplayValue , pas getColumnValue : le 1er
    // est le libellé human-readable que voit l'utilisateur , le 2nd
    // serait la clé technique - mélanger les deux casse la résolution
    // de référence dans <DatasLink> et provoque un 400 sur le clic ).
    const cellExpand = useCellExpand<DataRowResult, Component>({
      getRowKey: row => row?.naturalKey ?? "",
      getColKey: col => col?.id ?? "",
      getCellText: (row, col) => {
        if (!col?.getDisplayValue) return "";
        const v = col.getDisplayValue(row, dt.locale as string);
        return v == null ? "" : String(v);
      },
    });
    return { ...dt, ...cellExpand };
  },
  data() {
    return {
      // #475 - Etat du tri pour afficher la bonne fleche dans le slot #header
      sortField: "",
      sortOrder: "asc",
      type: window.location.href.split("/"),
    };
  },
  computed: {
    // Tri initial passé à Buefy : trie effectivement les données sur la
    // première colonne en ascendant au chargement. L'utilisateur voit
    // l'ordre cohérent avec la flèche affichée.
    defaultSort() {
      if ((this as any).dataColumnsToBeShown?.length > 0) {
        return ["values." + (this as any).dataColumnsToBeShown[0].id, "asc"];
      }
      return undefined;
    },
  },
  watch: {
    // Initialise sortField dès que les colonnes sont disponibles , pour
    // que sortIconFor/sortIconClass rendent la flèche ↑ sur la première
    // colonne au premier rendu ( affordance du tri ). On n'écrase pas un
    // tri déjà appliqué par l'utilisateur.
    dataColumnsToBeShown: {
      handler(newCols: any[]) {
        if (!this.sortField && newCols?.length > 0) {
          this.sortField = "values." + newCols[0].id;
          this.sortOrder = "asc";
        }
      },
      immediate: true,
    },
    // Remet la pagination sur la première page quand l'utilisateur change
    // le nombre de lignes par page ( évite de se retrouver sur une page
    // inexistante après réduction de la taille ).
    perPage() {
      (this as any).currentPage = 1;
    },
  },
  methods: {
    router() {
      return router;
    },
    // #475 - Met a jour l'etat du tri quand l'utilisateur clique sur un en-tete
    onSort(field: string, order: string) {
      this.sortField = field;
      this.sortOrder = order;
    },
    /**
     * Direction de tri courante pour une colonne donnée , consommée par
     * le composant {@code SortDoubleTriangle} dans le slot #header .
     * Remplace l'ancienne paire {@code sortIconFor / sortIconClass} qui
     * pilotait une b-icon Font-Awesome unique pivotante .
     */
    sortDirectionFor(field: string): "asc" | "desc" | "none" {
      if (this.sortField !== field) return "none";
      return this.sortOrder === "desc" ? "desc" : "asc";
    },
    // Toute la logique d'expand des cellules vit dans le composable
    // useCellExpand ( cf. setup ). Pas de méthodes inline ici.
  },
};
</script>

<style scoped>
/* Titre "Tableau d'un extrait" à gauche , sélecteur de pagination à droite. */
.table-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  /* Petite réserve à droite : sans cette marge le <b-select> "20"
     paraissait sortir du cadre du tableau ( image #284 ). */
  padding-right: 0.75rem;
  /* Image #51 / #53 : aérer la ligne du header tableau du bloc du
     dessus ( cadre Filter ou bandeau d'avertissement 500 lignes )
     et de la première ligne du tableau en dessous . */
  margin-top: 0.9em;
  margin-bottom: 0.7em;
}
/* Image #54 : compactage de la marge basse du bandeau d'avertissement
   "limite 500 lignes" quand il est affiché juste au-dessus du header
   du tableau . Sans cette règle , la marge Bulma par défaut empile
   avec le margin-top de `.table-header-row` et casse l'équilibre
   visuel entre les cas avec / sans bandeau . */
:deep(.notification.top-info-light) {
  margin-bottom: 0;
}
/* Image #50 : homogeneise la typographie de tous les libelles du
   header de tableau ( titre gauche + "Expand all" + "Rows per page"
   + select 20/50/100 ) sur la meme reference que le titre gauche . */
.table-header-row .titleSubmission,
.table-header-row :deep(.b-switch .control-label),
.table-header-row :deep(select),
.table-header-row :deep(.select select) {
  font-size: 0.95rem;
  font-weight: 400;
  color: #4a4a4a;
  font-style: normal;
}
/*
 * Bloc droit du header : switch global ( toujours rendu ) + sélecteur
 * "Lignes par page" ( conditionné rows >= 20 ). margin-left: auto pour
 * pousser tout à droite.
 */
.table-header-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
}
/*
 * Alias de l'ancien .rows-per-page conservé pour les css globaux qui
 * pourraient le viser ; à terme ce sélecteur peut être supprimé.
 */
.rows-per-page {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
}
/*
 * Le label hérite déjà de .subtitle / .titleSubmission ( Bulma ) pour
 * la taille et la couleur. On annule la marge basse de .columns .subtitle
 * imposée par Bulma pour garder l'alignement vertical avec le sélecteur.
 */
.rows-per-page-label {
  margin-bottom: 0 !important;
  display: inline-flex;
  align-items: center;
}
/*
 * Conteneur du <b-table> servant d'ancrage pour le spinner overlay
 * affiché pendant le re-render au changement de pagination.
 */
.table-area {
  position: relative;
}
/*
 * Overlay semi-opaque qui couvre le tableau pendant le re-render et
 * affiche le spinner. Le z-index reste modéré pour ne pas passer
 * au-dessus de la modale ou du menu de l'application.
 */
.pagination-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.7);
  z-index: 5;
}
/*
 * Bloc de chargement des données : spinner centré + message
 * d'attente discret , aligné verticalement.
 */
.data-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 2rem 0;
}
.data-loading-message {
  color: #4a4a4a;
  font-size: 0.95rem;
  margin: 0;
}

/*
 * Note : tout le CSS de troncature de cellule ( ellipse 1 ligne ,
 * wrap multi-ligne en mode déplié , bouton ▾ , overrides Bulma ) vit
 * désormais dans le composant <TruncatableCell> ( cf.
 * src/components/common/TruncatableCell.vue ). DataTableView ne
 * s'occupe plus que du wiring d'état via useCellExpand.
 */

/*
 * Tier 3 : switch "Tout déplier / Tout replier" dans le header du
 * tableau , collé à GAUCHE du sélecteur "Lignes par page" et séparé
 * par un trait vertical ( .table-header-divider ). Aligné dans
 * .rows-per-page ( flex ) , gap géré au niveau du parent.
 *
 * Réduction visuelle ( transform: scale 0.85 ) : Buefy is-small reste
 * un peu plus haut que le <b-select> voisin , du coup le switch
 * paraissait dominer dans la barre. Avec scale on revient à une
 * hauteur cohérente. transform-origin gauche pour ne pas créer
 * d'espace fantôme à gauche.
 */
.expand-all-switch {
  margin: 0;
}
.table-header-divider {
  display: inline-block;
  width: 1px;
  height: 1.4em;
  background: #dbdbdb;
  margin: 0 0.5rem;
  vertical-align: middle;
}
/* Note : la typographie du select 20/50/100 est gérée par la règle
   homogène `.table-header-row :deep(select)` ci-dessus ( image #50 ) . */

/*
 * Tier 4 : ▾ à l'EXTRÊME droite du header de colonne , poussé par
 * margin-left: auto dans le wrapper flex .header-flex. Bouton discret ,
 * @click.stop sur le bouton pour ne pas déclencher le tri natif Buefy.
 */
.header-flex {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  width: 100%;
}
.header-flex .header-main {
  flex: 1 1 auto;
  min-width: 0;
}
/*
 * Ancre flex pour l'indicateur de tri . Placé après {@code .header-main}
 * ( flex:1 qui pousse les éléments suivants à droite ) et avant
 * {@code .col-expand-toggle} ; centré verticalement par
 * {@code align-items: center} sur {@code .header-flex} .
 */
.sort-tri-anchor {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
}
.col-expand-toggle {
  flex: 0 0 auto;
  margin-left: auto;
  border: none;
  background: transparent;
  padding: 0 4px;
  cursor: pointer;
  color: #7a7a7a;
}
.col-expand-toggle:hover {
  color: var(--theme-primary, #167df0);
}
</style>
